import {
  alpha,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  FormLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Paper,
  Popover,
  Popper,
  Tooltip,
  Typography
} from '@mui/material';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import { useEffect, useRef, useState } from 'react';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useNavigate } from 'react-router';
import './notifications.css';
import { BaseAPI } from 'src/providers/base-api';
import { useConfirmDialog } from 'src/hooks/useConfirmDialog';
import StarBorder from '@mui/icons-material/StarBorder';
import { useTenantFeatures } from 'src/hooks/useTenantFeatures';
import QuickLinksCard from 'src/content/pages/Components/PostLoginLanding/QuickLinksCard';
import { NEAR_WHITE_F5F5F5, NEAR_WHITE_F0F0F0, GREY_5C5454 } from 'src/theme/tokens/colors';

// Interface for notification items
interface NotificationItem {
  id: string;
  title: string;
  message: string;
  timestamp: Date;
  isRead: boolean;
  type: 'warning' | 'info' | 'success' | 'error';
  link?: string;
  targetRole?: string; // Add this property
  employeeId?: string;
}

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    
    .MuiBadge-badge {
        background-color: ${alpha(theme.palette.error.main, 0.1)};
        color: ${theme.palette.error.main};
        min-width: 16px; 
        height: 16px;
        padding: 0;

        &::after {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            right: 10px;
            height: 100%;
            border-radius: 50%;
            box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
            content: "";
        }
    }
`
);

function HeaderNotifications() {
  const { confirm, notifySuccess, notifyError, notifyWarning, notifyInfo, Swal } = useConfirmDialog();
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const tenantFeatures = useTenantFeatures();
  const [category, setCategory] = useState<any>();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [quickLinkAnchorEl, setQuickLinkAnchorEl] = useState<null | HTMLElement>(null);
  const [isCrestWorthServer, setIsCrestWorthServer] = useState(false);

  const handleQuickLinkClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setQuickLinkAnchorEl(event.currentTarget);
  };

  const handleQuickLinkClose = () => {
    setQuickLinkAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const navigate = useNavigate();

  const OrganisationProfile = () => {
    // /nlaunch/org-settings is disabled (premature); the live org-settings page
    // is served by the /organisationprofile route (legacy route, now OrgSettingsV2).
    navigate('/organisationprofile');
    setAnchorEl(null);
  };

  const WorkLocations = () => {
    navigate('/work-locations/list');
    setAnchorEl(null);
  };

  const Departments = () => {
    navigate('/department/list');
    setAnchorEl(null);
  };

  const Designation = () => {
    navigate('/designation/list');
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleOpen = (): void => {
    setOpen(true);
    // Mark all notifications as read when opening the notification panel
    if (notifications.length > 0) {
      const updatedNotifications = notifications.map((notification) => ({
        ...notification,
        isRead: true
      }));
      setNotifications(updatedNotifications);
      sessionStorage.setItem('notifications', JSON.stringify(updatedNotifications));
      setUnreadCount(0);
    }
  };

  const navigateToNotificationPage = () => {
    navigate('/notifications');
    handleClose();
  };

  const handleNotificationClick = (notification: NotificationItem) => {
    // Mark the notification as read
    const updatedNotifications = notifications.map((item) => (item.id === notification.id ? { ...item, isRead: true } : item));

    setNotifications(updatedNotifications);
    sessionStorage.setItem('notifications', JSON.stringify(updatedNotifications));

    // Calculate new unread count
    const newUnreadCount = updatedNotifications.filter((item) => !item.isRead).length;
    setUnreadCount(newUnreadCount);

    // Navigate to the link if provided
    if (notification.link) {
      navigate(notification.link);
      handleClose();
    }
  };

  const getSideBarDate = async () => {
    const dataSidebar = JSON.parse(sessionStorage.getItem('operations') || '[]');
    dataSidebar.map((dataValue: any) => {
      switch (dataValue.refName) {
        case 'Settings': {
          setCategory(dataValue?.refName);
          break;
        }

        default: {
          break;
        }
      }
    });
  };

  // Function to check if user has a specific role
  const checkUserHasRole = (roleName) => {
    const userData = JSON.parse(sessionStorage.getItem('UserDetail') || '{}');

    if (!userData || !userData.roles) {
      console.error('No user data or roles found');
      return false;
    }

    // Check if user has the specified role
    const hasRole = userData.roles.some((role) => role.name === roleName || role.refName === roleName);
    return hasRole;
  };

  // Function to check asset availability notifications
  const checkAvailableAssets = (assets) => {
    const availableAssets = assets.filter((asset) => asset.assetStatus === 'Available');
    const count = availableAssets.length;

    const isLow = count <= 3;

    // Create notification if assets are low
    if (isLow) {
      createLowAssetNotification(count);
    }
  };

  // Function to create low asset notification
  const createLowAssetNotification = (count) => {
    const today = new Date().toDateString();

    // Create notification object
    const newNotification = {
      id: 'asset-' + Date.now(),
      title: 'Low Asset Availability Alert',
      message: `There are only ${count} assets with "Available" status. Consider procuring more assets.`,
      timestamp: new Date(),
      isRead: false,
      type: 'warning',
      link: '/assetdetails/list',
      targetRole: 'Asset Management'
    };

    // Add to notifications
    createRoleSpecificNotification(newNotification, 'Asset Management');
  };

  // Function to check warranty expirations
  const checkWarrantyExpirations = (assets) => {
    const today = new Date();
    const threeDaysFromNow = new Date();
    threeDaysFromNow.setDate(today.getDate() + 3);

    // Filter assets with warranties expiring in the next 3 days
    const expiringWarranties = assets.filter((asset) => {
      if (!asset.warrantyEndDate) {
        return false;
      }

      // Ensure warrantyEndDate is a Date object
      const warrantyEnd = new Date(asset.warrantyEndDate);

      // Check if the warranty is expiring in the next 3 days
      const isExpiring = warrantyEnd >= today && warrantyEnd <= threeDaysFromNow;
      if (isExpiring) {
        console.error(`Asset ${asset.assetId} has warranty expiring soon!`);
      }
      return isExpiring;
    });

    // Create notifications for each expiring warranty
    if (expiringWarranties.length > 0) {
      expiringWarranties.forEach((asset) => {
        createWarrantyExpirationNotification(asset);
      });
    }
  };

  // Function to create warranty expiration notification
  const createWarrantyExpirationNotification = (asset) => {
    const warrantyDate = new Date(asset.warrantyEndDate);
    const formattedDate = `${warrantyDate.getDate().toString().padStart(2, '0')}-${(warrantyDate.getMonth() + 1).toString().padStart(2, '0')}-${warrantyDate.getFullYear()}`;

    const newNotification = {
      id: 'warranty-' + Date.now() + '-' + asset.assetId,
      title: 'Warranty Expiring Soon',
      message: `Warranty for asset ${asset.assetId} (${asset.assetType} - ${asset.make}) is expiring on ${formattedDate}. Consider renewal.`,
      timestamp: new Date(),
      isRead: false,
      type: 'warning',
      link: `/assetdetails/${asset.id}?mode=view`,
      targetRole: 'Asset Management'
    };

    // Create role-specific notification
    createRoleSpecificNotification(newNotification, 'Asset Management');
  };

  // Function to create role-specific notification
  const createRoleSpecificNotification = (notification, targetRole) => {
    // Get existing notifications from sessionStorage
    const storedNotifications = sessionStorage.getItem('notifications');
    const existingNotifications = storedNotifications ? JSON.parse(storedNotifications) : [];

    // Add the target role to the notification
    const roleSpecificNotification = {
      ...notification,
      targetRole: targetRole
    };

    // Check if notification with same ID already exists
    const notificationExists = existingNotifications.some((item) => item.id === roleSpecificNotification.id);

    if (!notificationExists) {
      // Add to beginning of array
      existingNotifications.unshift(roleSpecificNotification);

      // Store back in sessionStorage
      sessionStorage.setItem('notifications', JSON.stringify(existingNotifications));

      // Update local state
      checkAssetAvailabilityNotifications();
    }
  };

  // Add this to the checkAssetAvailabilityNotifications function in index.tsx
  // to filter notifications by employee

  // In index.tsx - Update the checkAssetAvailabilityNotifications function:
  const checkAssetAvailabilityNotifications = () => {
    // Get notifications from sessionStorage or initialize empty array
    const storedNotifications = sessionStorage.getItem('notifications');
    const parsedNotifications: NotificationItem[] = storedNotifications ? JSON.parse(storedNotifications) : [];

    // Get current employee ID
    const userData = JSON.parse(sessionStorage.getItem('UserDetail') || '{}');
    const currentEmployeeId = userData?.employeeId;

    // Filter notifications based on role and employee ID
    const userNotifications = parsedNotifications.filter((notification) => {
      // If notification has employeeId, only show to that employee
      if (notification.employeeId) {
        return notification.employeeId === currentEmployeeId;
      }

      // Otherwise, use role-based filtering
      if (!notification.targetRole) return true;
      return checkUserHasRole(notification.targetRole);
    });

    setNotifications(userNotifications);

    // Count unread notifications
    const unread = userNotifications.filter((notification) => !notification.isRead).length;
    setUnreadCount(unread);
  };
  // Function to fetch asset data and generate notifications
  const fetchAssetsAndGenerateNotifications = async () => {
    try {
      const result = await new Promise((resolve, reject) => {
        BaseAPI.getAllAssetDetails((response: any) => {
          if (response.status === 200) {
            resolve(response.data);

            // Check for available assets count
            checkAvailableAssets(response.data);

            // Check for warranty expirations
            checkWarrantyExpirations(response.data);
          } else {
            reject(new Error(response.message || 'Failed to fetch asset details'));
          }
        });
      });
    } catch (error) {
      console.error('Error fetching asset details for notifications:', error);
    }
  };

  // Drives the Settings icon's visibility (category === 'Settings') — previously
  // only ran after the user opened the Quick Link menu at least once; now runs
  // on mount since the Quick Link menu no longer owns this file's data fetching.
  useEffect(() => {
    getSideBarDate();
  }, []);

  // Function to format date
  const formatNotificationDate = (date: Date) => {
    const now = new Date();
    const notificationDate = new Date(date);

    // If it's today
    if (now.toDateString() === notificationDate.toDateString()) {
      return notificationDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    // If it's in the last week
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    if (notificationDate > weekAgo) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days[notificationDate.getDay()];
    }

    // Otherwise, show the date
    return notificationDate.toLocaleDateString();
  };

  useEffect(() => {
    const appServerName = process.env.REACT_APP_SERVER_NAME;
    if (appServerName) {
      const isCresWorth = appServerName === 'crestworth';
      setIsCrestWorthServer(isCresWorth);
    }
  }, []);
  return (
    <>
      {!isCrestWorthServer && (
        <>
          {(tenantFeatures.quickLinksWFHEnabled || tenantFeatures.quickLinksPermissionEnabled || tenantFeatures.quickLinksEmergencyEnabled) && (
            <>
              <Tooltip arrow title="Quick Link">
                <IconButton color="primary" onClick={handleQuickLinkClick}>
                  <img src="/static/images/logo/quick_link.jpg" alt="Quick Link" style={{ width: 24, height: 24 }} />
                </IconButton>
              </Tooltip>

              <Popover
                anchorEl={quickLinkAnchorEl}
                open={Boolean(quickLinkAnchorEl)}
                onClose={handleQuickLinkClose}
                PaperProps={{
                  style: {
                    minWidth: 200
                  }
                }}
              >
                <QuickLinksCard />
              </Popover>
            </>
          )}

          <Tooltip arrow title="Notifications">
            <IconButton color="primary" ref={ref} onClick={handleOpen}>
              <NotificationsBadge
                badgeContent={unreadCount}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
              >
                <NotificationsActiveTwoToneIcon />
              </NotificationsBadge>
            </IconButton>
          </Tooltip>
        </>
      )}
      {category == 'Settings' ? (
        <Tooltip arrow title="Settings">
          <IconButton color="primary" ref={ref} aria-describedby={id} onClick={handleClick}>
            <NotificationsBadge
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
            >
              <SettingsOutlinedIcon />
            </NotificationsBadge>
          </IconButton>
        </Tooltip>
      ) : (
        <></>
      )}

      <Popover
        sx={{ top: '70px', marginRight: '10px', backdropFilter: 'blur(0px)' }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
      >
        <Box sx={{ p: 2 }} display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5">Notifications</Typography>
          <Button onClick={navigateToNotificationPage} variant="text" size="small">
            View All
          </Button>
        </Box>
        <Divider />
        <List sx={{ p: 0 }}>
          {notifications.length > 0 ? (
            notifications.slice(0, 5).map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  p: 2,
                  minWidth: 350,
                  display: { xs: 'block', sm: 'flex' },
                  backgroundColor: notification.isRead ? 'inherit' : alpha(NEAR_WHITE_F5F5F5, 0.3),
                  '&:hover': {
                    backgroundColor: alpha(NEAR_WHITE_F0F0F0, 0.5),
                    cursor: 'pointer'
                  }
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                <Box flex="1">
                  <Box display="flex" alignItems="center">
                    {notification.type === 'warning' && <WarningAmberIcon color="warning" sx={{ mr: 1 }} />}
                    <Box>
                      <Typography sx={{ fontWeight: 'bold' }}>{notification.title}</Typography>
                      <Typography variant="caption" sx={{ textTransform: 'none', display: 'block', mb: 1 }}>
                        {formatNotificationDate(notification.timestamp)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {notification.message}
                  </Typography>
                </Box>
              </ListItem>
            ))
          ) : (
            <ListItem sx={{ p: 2, minWidth: 350, display: { xs: 'block', sm: 'flex' } }}>
              <Box flex="1">
                <Typography component="span" variant="body2" color="text.secondary">
                  No new notifications
                </Typography>
              </Box>
            </ListItem>
          )}
        </List>
      </Popover>
      <Popover
        sx={{ top: '70px', marginRight: '10px', backdropFilter: 'blur(0px)' }}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disablePortal={false}
      >
        <Box sx={{ p: 2 }} display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h3">Settings</Typography>
        </Box>
        <Divider />
        <List sx={{ p: 0 }}>
          <ListItem sx={{ p: 1, minWidth: 350, display: { xs: 'block', sm: 'flex' } }}>
            <Box flex="1" sx={{ display: 'contents' }}>
              <CorporateFareOutlinedIcon />
              <Box display="flex" justifyContent="space-between">
                <Button sx={{ color: GREY_5C5454 }} onClick={() => OrganisationProfile()}>
                  Organisation Profile
                </Button>
              </Box>
            </Box>
          </ListItem>
        </List>
        <List sx={{ p: 0 }}>
          <ListItem sx={{ p: 1, minWidth: 350, display: { xs: 'block', sm: 'flex' } }}>
            <Box flex="1" sx={{ display: 'contents' }}>
              <LocationOnOutlinedIcon />
              <Box display="flex" justifyContent="space-between">
                <Button sx={{ color: GREY_5C5454 }} onClick={() => WorkLocations()}>
                  Work Locations
                </Button>
              </Box>
            </Box>
          </ListItem>
        </List>
        <List sx={{ p: 0 }}>
          <ListItem sx={{ p: 1, minWidth: 350, display: { xs: 'block', sm: 'flex' } }}>
            <Box flex="1" sx={{ display: 'contents' }}>
              <LanOutlinedIcon />
              <Box display="flex" justifyContent="space-between">
                <Button sx={{ color: GREY_5C5454 }} onClick={() => Departments()}>
                  Departments{' '}
                </Button>
              </Box>
            </Box>
          </ListItem>
        </List>
        <List sx={{ p: 0 }}>
          <ListItem sx={{ p: 1, minWidth: 350, display: { xs: 'block', sm: 'flex' } }}>
            <Box flex="1" sx={{ display: 'contents' }}>
              <AssignmentIndOutlinedIcon />
              <Box display="flex" justifyContent="space-between">
                <Button sx={{ color: GREY_5C5454 }} onClick={() => Designation()}>
                  Designation{' '}
                </Button>
              </Box>
            </Box>
          </ListItem>
        </List>
      </Popover>

    </>
  );
}

export default HeaderNotifications;
